import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiCheck, FiX } from 'react-icons/fi';
import { organizationAPI, type PendingInvitation } from '../../api/organizations';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useToast } from '../../hooks/useToast';
import { Button } from '../ui/Button/Button';
import { Badge } from '../ui/Badge/Badge';
import { ConfirmModal } from '../Modal/Modal';
import styles from './PendingInvitationsBanner.module.css';

import { logger } from '../../utils/logger';

export function PendingInvitationsBanner() {
  const { isAuthenticated } = useAuth();
  const { switchOrganization } = useOrganization();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [acceptedOrganizations, setAcceptedOrganizations] = useState<Record<string, string>>({});
  const [dismissed, setDismissed] = useState(false);
  // P1.2: in-app confirm dialog in place of window.confirm().
  const [declineTarget, setDeclineTarget] = useState<PendingInvitation | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadPendingInvitations();
    }
  }, [isAuthenticated]);

  const loadPendingInvitations = async () => {
    try {
      setLoading(true);
      const response = await organizationAPI.getPendingInvitations();
      setInvitations(response.invitations);
    } catch (error: any) {
      // Silently fail - don't show error for this optional feature
      logger.error('Failed to load pending invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitation: PendingInvitation) => {
    try {
      setActionLoading(invitation.id);
      let orgId = acceptedOrganizations[invitation.id];
      if (!orgId) {
        const response = await organizationAPI.acceptInvitation(invitation.token);
        orgId = response.organization.id;
        setAcceptedOrganizations(prev => ({ ...prev, [invitation.id]: orgId }));
      }

      await switchOrganization(orgId, { reload: false });
      navigate('/dashboard');
      window.location.reload();
    } catch (error: any) {
      addToast(error.message || 'Failed to accept invitation', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmDecline = async () => {
    if (!declineTarget) return;
    const invitation = declineTarget;

    try {
      setActionLoading(invitation.id);
      await organizationAPI.declineInvitation(invitation.token);
      addToast(`Declined invitation to ${invitation.organization.name}`, 'info');

      // Remove from list
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
    } catch (error: any) {
      addToast(error.message || 'Failed to decline invitation', 'error');
    } finally {
      setActionLoading(null);
      setDeclineTarget(null);
    }
  };

  // Don't render if not authenticated, loading, no invitations, or dismissed
  if (!isAuthenticated || loading || invitations.length === 0 || dismissed) {
    return null;
  }

  return (
    <div className={styles.banner}>
      <div className={styles.bannerContent}>
        <div className={styles.bannerHeader}>
          <FiMail className={styles.icon} />
          <span className={styles.title}>
            You have {invitations.length} pending invitation{invitations.length !== 1 ? 's' : ''}
          </span>
          <button
            className={styles.dismissButton}
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
          >
            <FiX />
          </button>
        </div>

        <div className={styles.invitationsList}>
          {invitations.map((invitation) => (
            <div key={invitation.id} className={styles.invitationItem}>
              <div className={styles.invitationInfo}>
                <span className={styles.orgName}>{invitation.organization.name}</span>
                <Badge variant="secondary" size="sm">{invitation.role}</Badge>
                <span className={styles.invitedBy}>
                  Invited by {invitation.invited_by.first_name} {invitation.invited_by.last_name}
                </span>
              </div>

              <div className={styles.actions}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleAccept(invitation)}
                  disabled={actionLoading !== null}
                >
                  <FiCheck />
                  {acceptedOrganizations[invitation.id] ? 'Open organization' : 'Accept'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setDeclineTarget(invitation)}
                  disabled={actionLoading !== null || !!acceptedOrganizations[invitation.id]}
                >
                  <FiX />
                  Decline
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ConfirmModal
        isOpen={declineTarget !== null}
        onClose={() => setDeclineTarget(null)}
        onConfirm={confirmDecline}
        title="Decline invitation"
        message={
          declineTarget
            ? `Are you sure you want to decline the invitation to ${declineTarget.organization.name}?`
            : ''
        }
        confirmText="Decline"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

